import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { createClientCoupon } from 'crud/payments_api/client_coupons';
import { ClientLocal } from 'views/Restaurant/ClientManagement/Clients/types/client.types';
import { FaQrcode } from 'react-icons/fa';
import UserTypeSelector from './components/UserTypeSelector/UserTypeSelector';
import ClientSearch from './components/ClientSearch/ClientSearch';
import CouponDisplay from './components/CouponDisplay/CouponDisplay';
import WhatsAppInput from './components/WhatsAppInput/WhatsAppInput';
import './CreateClientCoupon.scss';

interface CreateClientCouponProps {
  show: boolean;
  setShow: (show: boolean) => void;
  selectedCoupon: any; // Must contain payment_coupon_id property
  setSelectedCoupon: (coupon: any) => void;
  Maps?: any;
}

const CreateClientCoupon: React.FC<CreateClientCouponProps> = ({
  show,
  setShow,
  selectedCoupon,
  setSelectedCoupon,
}) => {
  // User type: null means not selected yet.
  const [userType, setUserType] = useState<'anonymous' | 'registered' | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientLocal | null>(null);
  const [couponCode, setCouponCode] = useState<string>('');
  const [generatedUrl, setGeneratedUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [codeCreated, setCodeCreated] = useState<boolean>(false);
  const [codeFailed, setCodeFailed] = useState<boolean>(false);
  const [showWhatsAppInput, setShowWhatsAppInput] = useState<boolean>(false);

  const handleUserTypeChange = (type: 'anonymous' | 'registered') => {
    setUserType(type);
    setSelectedClient(null);
    setShowWhatsAppInput(false);
  };

  const handleClientSelect = (client: ClientLocal) => {
    setSelectedClient(client);
  };

  const handleGenerateCoupon = async () => {
    setLoading(true);
    try {
      // Use the registered client's ID if userType is 'registered', else null
      const clientId = userType === 'registered' && selectedClient ? selectedClient.client_id : null;
      const response: any = await createClientCoupon(clientId, selectedCoupon.payment_coupon_id);
      if (response && response.data) {
        const code = response.data.coupon_code;
        setCouponCode(code);
        // Build dynamic URL: base URL + "/ClientCouponLanding/" + code
        const url = `${window.location.origin}/ClientCouponLanding/${code}`;
        setGeneratedUrl(url);
        setCodeCreated(true);
        toast.success('Cupón asignado exitosamente');
      }
    } catch (error) {
      console.error('Error creating client coupon:', error);
      setCodeFailed(true);
      toast.error('Error asignando cupón');
    }
    setLoading(false);
  };

  const handleCopyToClipboard = async () => {
    if (generatedUrl) {
      try {
        await navigator.clipboard.writeText(generatedUrl);
        toast.success('URL copiada al portapapeles');
      } catch (error) {
        console.error(error);
        toast.error('Error copiando al portapapeles');
      }
    }
  };

  const handleWhatsAppShare = (phone?: string, countryCode?: string) => {
    if (!generatedUrl) return;
    
    let fullPhone = '';
    
    if (phone && countryCode) {
      // Phone provided via WhatsAppInput component
      const cleanPhone = phone.replace(/\s/g, '');
      
      // Build full phone number with country prefix
      if (countryCode === 'CL') {
        fullPhone = `+569${cleanPhone}`;
      } else if (countryCode === 'AR') {
        fullPhone = `+549${cleanPhone}`;
      } else if (countryCode === 'CO') {
        fullPhone = `+57${cleanPhone}`;
      } else if (countryCode === 'PE') {
        fullPhone = `+51${cleanPhone}`;
      }
    } else if (selectedClient?.phone_number) {
      // Use selected client's phone number
      fullPhone = selectedClient.phone_number;
    } else {
      toast.error('Por favor ingresa un número de teléfono');
      return;
    }

    const formattedText = `Hola, te he enviado un cupón de descuento. Puedes verlo aquí: ${generatedUrl}`;
    const whatsappUrl = `https://wa.me/${fullPhone}?text=${encodeURIComponent(formattedText)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleClose = () => {
    setShow(false);
    setCodeCreated(false);
    setCodeFailed(false);
    setCouponCode('');
    setGeneratedUrl('');
    setSelectedClient(null);
    setSelectedCoupon(null);
    setUserType(null);
    setShowWhatsAppInput(false);
  };

  return (
    <Modal show={show} className="modal-cupones create-client-coupon-modal" size="lg" centered>
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Generar cupón para cliente</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {/* Step Indicator */}  
        <div className="step-indicator">
          <div className={`step ${userType ? 'completed' : 'active'}`}>
            <div className="step-number">1</div>
            <span>Tipo de cliente</span>
          </div>
          <div className={`step ${userType && !codeCreated ? 'active' : userType && codeCreated ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <span>Generar código</span>
          </div>
          <div className={`step ${codeCreated ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Compartir</span>
          </div>
        </div>

        {/* User Type Selection */}
        {!userType && (
          <UserTypeSelector onSelectType={handleUserTypeChange} />
        )}

        {/* Client Search Section */}
        {userType === 'registered' && !codeCreated && (
          <ClientSearch 
            onClientSelect={handleClientSelect}
            selectedClient={selectedClient}
          />
        )}

        {/* Generate Coupon Section */}
        {userType && !codeCreated && (
          <div className="generate-section">
            <Button
              className="generate-button"
              onClick={handleGenerateCoupon}
              disabled={loading || (userType === 'registered' && !selectedClient)}
              size="lg"
            >
              {loading ? (
                <div className="generating-overlay">
                  <div className="custom-loading-spinner">
                    <div className="spinner-ring"></div>
                  </div>
                  <span className="generating-text">Generando cupón...</span>
                </div>
              ) : (
                <>
                  <FaQrcode /> Generar Código QR
                </>
              )}
            </Button>
          </div>
        )}

        {/* Coupon Display */}
        {codeCreated && !showWhatsAppInput && (
          <CouponDisplay
            couponCode={couponCode}
            generatedUrl={generatedUrl}
            onCopyToClipboard={handleCopyToClipboard}
            onWhatsAppShare={() => {
              if (selectedClient?.phone_number) {
                handleWhatsAppShare();
              } else {
                setShowWhatsAppInput(true);
              }
            }}
            onShowWhatsAppInput={() => setShowWhatsAppInput(true)}
            showWhatsAppButton={!!selectedClient?.phone_number}
            clientPhone={selectedClient?.phone_number}
          />
        )}

        {/* WhatsApp Input */}
        {codeCreated && showWhatsAppInput && (
          <WhatsAppInput
            onSend={(phone, countryCode) => {
              handleWhatsAppShare(phone, countryCode);
              setShowWhatsAppInput(false);
            }}
            onCancel={() => setShowWhatsAppInput(false)}
          />
        )}

        {/* Error State */}
        {codeFailed && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h6>Error al generar el cupón</h6>
            <p>Ocurrió un problema. Por favor, inténtalo nuevamente.</p>
            <Button className="retry-button" onClick={() => setCodeFailed(false)}>
              Intentar de nuevo
            </Button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" variant="none" onClick={handleClose}>
            {codeCreated ? 'Finalizar' : 'Cancelar'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateClientCoupon;
